import { AlertTriangle, Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * El diff de una replanificacion, antes de escribirla.
 *
 * Arrastrar una barra no mueve una barra: corre las que dependen de ella, puede empujar el
 * compromiso del caso por encima de lo prometido, y puede dejar a alguien con mas trabajo del
 * que entra en su dia. Nada de eso se ve al soltar — aparece despues, ya guardado, y hay que
 * deshacerlo a mano.
 *
 * Por eso soltar **simula**. Este panel es lo que se ve entre soltar y confirmar, y esta
 * ordenado por lo que decide: primero que se mueve, despues a que compromiso le pega, y al
 * final quien queda sobreasignado. Confirmar vuelve a calcular del lado del servidor: entre que
 * se vio esto y se apreto el boton, alguien pudo mover otra cosa.
 */

function fechaCorta(iso) {
  if (!iso) return '—';
  const [, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}`;
}

function Seccion({ titulo, children }) {
  return (
    <div className="space-y-1">
      <h3 className="text-[8.5px] font-mono uppercase tracking-[0.14em] text-zinc-400">{titulo}</h3>
      {children}
    </div>
  );
}

function veredicto(t) {
  if (t.estado === 'sin_compromiso') return { texto: 'sin cierre comprometido', clase: 'text-zinc-500 border-zinc-300' };
  if (t.estado === 'sin_plan') return { texto: 'sin plan', clase: 'text-zinc-500 border-zinc-300' };
  if (t.entra) {
    const m = t.margen_despues;
    return { texto: m === 0 ? 'entra justo' : `entra · ${m} d`, clase: 'text-zinc-600 border-zinc-300' };
  }
  return { texto: `no entra · ${Math.abs(t.margen_despues)} d`, clase: 'text-brand-danger border-red-200 bg-red-50' };
}

export default function GanttDiff({ diff, titulo, simulando, aplicando, error, onConfirmar, onDescartar }) {
  if (simulando && !diff) {
    return (
      <aside className="w-72 shrink-0 bg-white border border-brand-border rounded-sm p-4 flex items-center gap-2 text-xs text-zinc-500" data-testid="gantt-diff-cargando">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Calculando qué se mueve…
      </aside>
    );
  }
  if (error) {
    return (
      <aside className="w-72 shrink-0 bg-white border border-brand-border rounded-sm overflow-hidden" data-testid="gantt-diff-error">
        <div className="px-3 py-2 border-b border-brand-border bg-[#FFEFEC]">
          <div className="text-[8.5px] font-mono uppercase tracking-[0.14em] text-brand-danger">No se pudo simular</div>
        </div>
        <div className="p-3 text-[11px] text-zinc-700 leading-snug">{error}</div>
        <div className="border-t border-brand-border p-2">
          <Button size="sm" variant="outline" className="h-7 text-[10px] rounded-sm" onClick={onDescartar}>Cerrar</Button>
        </div>
      </aside>
    );
  }
  if (!diff) return null;

  const pedidas = (diff.movidas || []).filter((m) => m.motivo === 'pedido');
  const arrastradas = (diff.movidas || []).filter((m) => m.motivo !== 'pedido');
  const dedicaciones = (diff.movidas || []).filter(
    (m) => m.antes?.asignacion_pct !== m.despues?.asignacion_pct,
  );
  const pisadas = (diff.movidas || []).filter((m) => m.pisa_una_fecha_confirmada);

  return (
    <aside className="w-72 shrink-0 bg-white border border-brand-border rounded-sm flex flex-col overflow-hidden self-start max-h-[calc(100vh-9rem)] sticky top-0" data-testid="gantt-diff">
      <div className="px-3 py-2 border-b border-brand-border bg-[#FFFCF2]">
        <div className="text-[8.5px] font-mono uppercase tracking-[0.14em] text-[#8A6400]">
          Simulación · nada escrito todavía
        </div>
        <h2 className="text-xs font-semibold mt-0.5">
          {titulo || (pedidas.length === 1 ? pedidas[0].title : `${pedidas.length} cambios pedidos`)}
        </h2>
      </div>

      <div className="flex-1 overflow-auto px-3 py-3 space-y-3.5 text-[11px]">
        {(diff.movidas || []).length === 0 && (
          <p className="text-zinc-500 leading-snug">
            Con este movimiento no cambia ninguna fecha. Probablemente la tarea ya estaba ahí.
          </p>
        )}

        {pedidas.length > 0 && (
          <Seccion titulo="Se mueve">
            {pedidas.map((m) => (
              <div key={m.task_id} className="flex items-baseline gap-1.5">
                <span className="flex-1 truncate" title={m.title}>{m.title}</span>
                <span className="font-mono text-[10px] shrink-0">
                  {fechaCorta(m.antes?.planned_end)} → {fechaCorta(m.despues?.planned_end)}
                </span>
                {m.dias !== 0 && m.dias != null && (
                  <span className={`font-mono text-[10px] shrink-0 ${m.dias > 0 ? 'text-brand-danger' : 'text-zinc-500'}`}>
                    {m.dias > 0 ? '+' : ''}{m.dias} d
                  </span>
                )}
              </div>
            ))}
          </Seccion>
        )}

        {arrastradas.length > 0 && (
          <Seccion titulo={`Arrastra (${arrastradas.length})`}>
            {arrastradas.map((m) => (
              <div key={m.task_id} className="flex items-baseline gap-1.5 pl-2 text-zinc-500">
                <span className="flex-1 truncate" title={m.title}>↳ {m.title}</span>
                <span className="font-mono text-[10px] shrink-0">
                  {fechaCorta(m.antes?.planned_end)} → {fechaCorta(m.despues?.planned_end)}
                </span>
              </div>
            ))}
          </Seccion>
        )}

        {dedicaciones.length > 0 && (
          <Seccion titulo="Dedicación">
            {dedicaciones.map((m) => (
              <div key={m.task_id} className="flex items-baseline gap-1.5">
                <span className="flex-1 truncate">{m.title}</span>
                <span className="font-mono text-[10px] shrink-0">
                  {m.antes?.asignacion_pct ?? 100}% → {m.despues?.asignacion_pct}%
                </span>
              </div>
            ))}
            <p className="text-[9.5px] text-zinc-400 leading-snug">
              El esfuerzo estimado no cambia: estirar mueve la dedicación, no las horas.
            </p>
          </Seccion>
        )}

        {pisadas.length > 0 && (
          <div className="border border-amber-200 bg-[#FFFCF2] rounded-sm p-2 space-y-0.5">
            <p className="text-[10px] text-[#8A6400] leading-snug">
              {pisadas.length === 1 ? 'Una fecha' : `${pisadas.length} fechas`} que alguien había
              confirmado se {pisadas.length === 1 ? 'mueve' : 'mueven'} por la dependencia.
            </p>
            {pisadas.map((m) => (
              <p key={m.task_id} className="text-[9.5px] text-zinc-500 truncate">· {m.title}</p>
            ))}
          </div>
        )}

        {(diff.tickets || []).length > 0 && (
          <>
            <div className="h-px bg-brand-border" />
            <Seccion titulo="Compromisos">
              {(diff.tickets || []).map((t) => {
                const v = veredicto(t);
                return (
                  <div key={t.ticket_id} className="space-y-0.5">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-mono text-[10px] text-zinc-400 shrink-0">{t.ticket_id}</span>
                      <span className="font-mono text-[10px] ml-auto shrink-0">
                        {fechaCorta(t.plan_antes)} → {fechaCorta(t.plan_despues)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[9.5px] px-1 border rounded-sm font-mono ${v.clase}`}>{v.texto}</span>
                      {t.empeora && <span className="text-[9px] text-zinc-400">era {t.margen_antes} d</span>}
                    </div>
                  </div>
                );
              })}
            </Seccion>
          </>
        )}

        <div className="h-px bg-brand-border" />
        <Seccion titulo="Carga">
          {(diff.conflictos_nuevos || []).length === 0 && (diff.conflictos || []).length === 0 && (
            <p className="text-zinc-500 flex items-center gap-1.5">
              <Check className="w-3 h-3" /> Nadie queda sobreasignado.
            </p>
          )}
          {(diff.conflictos_nuevos || []).map((c) => (
            <div key={`${c.persona_id}-${c.desde}`} className="flex items-baseline gap-1.5 text-brand-danger">
              <AlertTriangle className="w-3 h-3 shrink-0" />
              <span className="flex-1 truncate font-mono text-[10px]">{c.persona_id}</span>
              <span className="font-mono text-[10px] shrink-0">{c.pico_pct}%</span>
            </div>
          ))}
          {(diff.conflictos_resueltos || []).map((c) => (
            <div key={`ok-${c.persona_id}-${c.desde}`} className="flex items-baseline gap-1.5 text-zinc-500">
              <Check className="w-3 h-3 shrink-0" />
              <span className="flex-1 truncate font-mono text-[10px]">{c.persona_id}</span>
              <span className="text-[9.5px] shrink-0">se resuelve</span>
            </div>
          ))}
          {(diff.conflictos_nuevos || []).length === 0 && (diff.conflictos || []).length > 0 && (
            <p className="text-[9.5px] text-zinc-400 leading-snug">
              Los {diff.conflictos.length} conflicto(s) que ya había siguen igual: este cambio no
              los crea ni los resuelve.
            </p>
          )}
        </Seccion>
      </div>

      <div className="border-t border-brand-border p-2 flex items-center gap-2">
        <Button
          size="sm"
          className="h-7 text-[10px] rounded-sm"
          onClick={onConfirmar}
          disabled={aplicando || (diff.movidas || []).length === 0}
          data-testid="gantt-diff-confirmar"
        >
          {aplicando ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
          Confirmar
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[10px] rounded-sm" onClick={onDescartar} disabled={aplicando} data-testid="gantt-diff-descartar">
          <X className="w-3 h-3 mr-1" /> Descartar
        </Button>
      </div>
    </aside>
  );
}
